/**
 * Export Data Button
 *
 * Icon-only export control for the Bill of Materials header bar. Clicking
 * immediately downloads the estimate as a data file (config metadata + BOM,
 * built by utils/csv.ts) — no dialog. User-facing name is
 * "Export Bill of Material (.CSV)" (tooltip/aria).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileDown } from 'lucide-react';
import { useEstimator } from '../../context/EstimatorContext';
import { downloadEstimateCsv } from '../../utils/csv';
import { trackCsvExported } from '../../utils/gtmTracking';
import { calculationModeLabel, colorLabel, orientationLabel, spaceTypeLabel } from './configDataPoints';
import './ExportDataButton.css';

interface ExportDataButtonProps {
  estimateId?: string;
  /** Pass estimate directly (e.g. from shared estimate page) to bypass context */
  estimate?: import('../../types').EstimateOutput;
  /** Saved/shared estimate name — used for the download filename. */
  estimateName?: string;
}

export const ExportDataButton: React.FC<ExportDataButtonProps> = ({ estimateId, estimate, estimateName }) => {
  const { t } = useTranslation('results');
  const { state } = useEstimator();
  const estimateResult = estimate || state.estimateResult;
  const { wallsConfig, ceilingConfig, applicationConfig, calculationMode } = state;

  const handleExport = () => {
    if (!estimateResult) return;

    trackCsvExported(calculationMode, estimateResult);

    // Metadata comes from the estimate's own input record — the config that
    // actually produced this BOM — not the live wizard context, which is
    // stale on shared-estimate pages and after mode changes.
    const input = estimateResult.input;
    const mode = input?.calculationMode || calculationMode;
    const wallCfg = input?.fullRoomWallConfig;
    const hasWalls = mode !== 'CeilingOnly';
    const includeCeiling = input ? input.includeCeiling : ceilingConfig.include;

    // Wall-by-wall estimates have no room: roomDimensions and the full-room
    // wall config are unused defaults there, so the header describes the walls
    // themselves (same convention as the on-screen summary in
    // configDataPoints). Mixed heights are omitted; mixed colors/orientations
    // list each value once.
    const isWallByWall = mode === 'IndividualWalls';
    const walls = (input?.individualWalls ?? []).filter(w => w.enabled);
    const wallHeights = [...new Set(walls.map(w => w.height))];
    const wallColors = [...new Set(walls.map(w => w.color))].join(', ');
    const wallOrientations = [...new Set(walls.map(w => w.orientation))].join(', ');
    const wallByWallDoors = walls.reduce(
      (sum, w) => sum + (w.garage?.enabled ? w.garage.numDoors : 0),
      0
    );

    const garage = wallCfg?.garage ?? applicationConfig.garage;
    const fullRoomDoors = garage?.enabled && garage.numDoors > 0 ? garage.numDoors : undefined;

    downloadEstimateCsv(estimateResult, {
      projectTitle: estimateName || estimateResult.projectInfo?.projectName,
      estimateUrl: estimateId ? `${window.location.origin}/estimate/${estimateId}` : undefined,
      projectType: calculationModeLabel(mode),
      spaceType: spaceTypeLabel(applicationConfig.spaceType),
      roomLengthFt: isWallByWall ? undefined : input?.roomDimensions?.length,
      roomWidthFt: isWallByWall ? undefined : input?.roomDimensions?.width,
      wallHeightFt: isWallByWall
        ? (wallHeights.length === 1 ? wallHeights[0] : undefined)
        : input?.roomDimensions?.height,
      wallColor: !hasWalls ? undefined : isWallByWall
        ? (wallColors ? colorLabel(wallColors) : undefined)
        : colorLabel(wallCfg?.wallColor ?? wallsConfig.color),
      wallOrientation: !hasWalls ? undefined : isWallByWall
        ? (wallOrientations ? orientationLabel(wallOrientations) : undefined)
        : orientationLabel(wallCfg?.wallOrientation ?? wallsConfig.orientation),
      ceilingIncluded: includeCeiling,
      ceilingColor: includeCeiling ? colorLabel(input?.ceilingConfig?.color ?? ceilingConfig.color) : undefined,
      garageDoors: isWallByWall ? (wallByWallDoors > 0 ? wallByWallDoors : undefined) : fullRoomDoors,
    });
  };

  return (
    <button
      type="button"
      className="bom-icon-btn"
      onClick={handleExport}
      disabled={!estimateResult}
      title={t('export.csv')}
      aria-label={t('export.csv')}
    >
      <FileDown size={18} />
    </button>
  );
};

export default ExportDataButton;
